import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, COUNTRY_FLAGS } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ExternalLink, FileText, Calendar, Globe, Lock, ArrowRight } from "lucide-react";

export default function NewsletterReader() {
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState("");
  const [newsletters, setNewsletters] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unlocked, setUnlocked] = useState(!!localStorage.getItem("bess_reader_token"));
  const [gateEmail, setGateEmail] = useState("");
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!unlocked) return;
    setLoading(true);
    api.get("/newsletters", { params: country ? { country } : {} })
      .then((r) => {
        setNewsletters(r.data.newsletters);
        setActive(r.data.newsletters[0] || null);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          setUnlocked(false);
          toast.info("Your reading session expired. Please enter your email again.");
        }
      })
      .finally(() => setLoading(false));
  }, [country, unlocked]);

  const verify = async (e) => {
    e.preventDefault();
    if (!gateEmail) return;
    setVerifying(true);
    try {
      const r = await api.post("/reader/verify", { email: gateEmail });
      localStorage.setItem("bess_reader_token", r.data.token);
      setUnlocked(true);
      toast.success("Welcome back. Here are your briefs.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not verify this email.");
    } finally {
      setVerifying(false);
    }
  };

  const grouped = active
    ? active.items.reduce((acc, it) => {
        (acc[it.category_label] = acc[it.category_label] || []).push(it);
        return acc;
      }, {})
    : {};

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
        <SiteHeader />
        <div className="max-w-lg mx-auto px-6 py-24">
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#737373] mb-3 flex items-center gap-2"><Lock className="w-3 h-3" /> Subscribers only</div>
          <h1 className="font-display font-black tracking-tighter text-4xl lg:text-5xl mb-4">THE BRIEFS</h1>
          <p className="text-[#404040] mb-8 leading-relaxed">The brief archive is available to subscribers. Enter the email you subscribed with to unlock it.</p>
          <form onSubmit={verify} className="space-y-4" data-testid="reader-gate-form">
            <Input
              type="email"
              value={gateEmail}
              onChange={(e) => setGateEmail(e.target.value)}
              placeholder="you@cleanhorizon.com"
              className="rounded-none border-2 border-[#0A0A0A] bg-white h-12 text-base focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-0"
              data-testid="reader-gate-email"
            />
            <button type="submit" disabled={verifying} className="w-full h-12 bg-volt text-black font-display font-bold uppercase tracking-tight flex items-center justify-center gap-2 border-2 border-black transition-colors hover:bg-black hover:text-volt disabled:opacity-50" data-testid="reader-gate-submit">
              {verifying ? "Checking…" : "Unlock briefs"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          <p className="mt-6 text-sm text-[#737373]">Not subscribed yet? <Link to="/" className="underline font-semibold text-[#0A0A0A]" data-testid="reader-gate-signup-link">Sign up here</Link>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <SiteHeader />
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
          <h1 className="font-display font-black tracking-tighter text-4xl lg:text-5xl">THE BRIEFS</h1>
          <p className="text-sm text-[#737373] font-mono">Published archive · updated bi-weekly</p>
        </div>

        {/* Country filter */}
        <div className="flex flex-wrap gap-0 border-l border-t border-[#E5E5E5] mb-10">
          <button onClick={() => setCountry("")} className={`border-r border-b border-[#E5E5E5] px-4 py-2 text-xs font-mono tracking-wider transition-colors ${country === "" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#f0f0f0]"}`} data-testid="filter-all">ALL</button>
          {countries.map((c) => (
            <button key={c.code} onClick={() => setCountry(c.code)} className={`border-r border-b border-[#E5E5E5] px-4 py-2 text-xs font-mono tracking-wider transition-colors ${country === c.code ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#f0f0f0]"}`} data-testid={`filter-${c.code}`}>
              {COUNTRY_FLAGS[c.code]} {c.code}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center font-mono text-sm text-[#737373]">Loading…</div>
        ) : newsletters.length === 0 ? (
          <div className="border border-dashed border-[#ccc] py-20 text-center" data-testid="empty-state">
            <FileText className="w-10 h-10 mx-auto text-[#ccc] mb-4" />
            <p className="font-display font-bold text-xl">No briefs published yet.</p>
            <p className="text-[#737373] mt-2 text-sm">The first brief will appear here once research runs (every other Monday 08:00 CET) or an admin triggers it.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[300px_1fr] gap-12">
            {/* Sidebar list */}
            <aside className="lg:sticky lg:top-24 self-start space-y-0 border-t border-[#E5E5E5]">
              {newsletters.map((nl) => (
                <button
                  key={nl.id}
                  onClick={() => setActive(nl)}
                  className={`w-full text-left border-b border-[#E5E5E5] p-4 transition-colors ${active?.id === nl.id ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-[#f0f0f0]"}`}
                  data-testid={`brief-item-${nl.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold tracking-tight">{COUNTRY_FLAGS[nl.country_code]} {nl.country_name}</span>
                    <span className={`font-mono text-xs ${active?.id === nl.id ? "text-volt" : "text-[#737373]"}`}>{nl.item_count}</span>
                  </div>
                  <div className={`font-mono text-[11px] mt-1 ${active?.id === nl.id ? "text-white/60" : "text-[#737373]"}`}>{nl.week_label}</div>
                </button>
              ))}
            </aside>

            {/* Article */}
            {active && (
              <article className="max-w-3xl animate-fadein" key={active.id} data-testid="active-brief">
                <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#737373] mb-2">{active.week_label} · {active.start_date} → {active.end_date}</div>
                <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl mb-8">
                  {COUNTRY_FLAGS[active.country_code]} {active.country_name}
                </h2>
                {active.items.length === 0 ? (
                  <p className="text-[#737373]">No qualifying developments were found for this market in the covered window.</p>
                ) : (
                  Object.entries(grouped).map(([label, items]) => (
                    <section key={label} className="mb-12">
                      <h3 className="font-display font-bold text-lg border-b-2 border-[#0A0A0A] pb-2 mb-6 flex items-center gap-2">
                        <span className="w-2 h-2 bg-volt inline-block" /> {label}
                      </h3>
                      <div className="space-y-8">
                        {items.map((it, i) => (
                          <div key={i} data-testid="news-item">
                            <h4 className="font-display font-bold text-xl tracking-tight leading-snug">{it.title}</h4>
                            <div className="flex items-center gap-4 mt-2 text-xs text-[#737373] font-mono">
                              {it.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{it.date}</span>}
                              {it.source_language && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{it.source_language}</span>}
                            </div>
                            <p className="mt-3 text-[#262626] leading-relaxed">{it.summary}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {it.sources.map((s, j) => (
                                <a key={j} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-mono border border-[#0A0A0A] px-2 py-1 transition-colors hover:bg-volt" data-testid="source-link">
                                  <ExternalLink className="w-3 h-3" /> {s.name || "Source"}
                                </a>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </section>
                  ))
                )}
              </article>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
